import { useState, useRef, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Upload,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  Download,
  FileUp,
  Eye,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SAMPLE = `email,name,user_type,teacher_grade_level
sarah.anderson@iltexas.org,Sarah Anderson,teacher,6
marcus.reed@iltexas.org,Dr. Marcus Reed,assistant_principal,
maria.lopez@iltexas.org,Maria Lopez,ea_staff,
sarah.bennett@iltexas.org,Sarah Bennett,driveline,
`;

const TYPE_ALIASES = {
  teacher: "teacher",
  ap: "assistant_principal",
  assistant_principal: "assistant_principal",
  "assistant principal": "assistant_principal",
  ea: "ea_staff",
  ea_staff: "ea_staff",
  "ea staff": "ea_staff",
  driveline: "driveline",
  driveline_staff: "driveline",
};

const splitLine = (line, delim) => {
  const out = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQ) {
      if (c === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (c === '"') inQ = false;
      else cur += c;
    } else {
      if (c === '"') inQ = true;
      else if (c === delim) {
        out.push(cur);
        cur = "";
      } else cur += c;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
};

const parseRaw = (raw) => {
  const lines = raw.replace(/\r\n/g, "\n").split("\n").filter((l) => l.trim());
  if (!lines.length) return { headers: [], rows: [] };
  const first = lines[0];
  const delim = (first.match(/\t/g) || []).length >= (first.match(/,/g) || []).length ? "\t" : ",";
  const headers = splitLine(first, delim).map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
  const rows = lines.slice(1).map((line) => {
    const values = splitLine(line, delim);
    const obj = {};
    headers.forEach((h, i) => (obj[h] = (values[i] ?? "").trim()));
    if (obj.user_type) obj.user_type = TYPE_ALIASES[obj.user_type] || obj.user_type;
    return obj;
  });
  return { headers, rows };
};

const rowsToCSV = (headers, rows) => {
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.map(esc).join(","), ...rows.map((r) => headers.map((h) => esc(r[h] ?? "")).join(","))].join("\n");
};

export default function BulkAddStaff({ onClose, onSuccess }) {
  const [text, setText] = useState("");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const parsed = useMemo(() => (text.trim() ? parseRaw(text) : { headers: [], rows: [] }), [text]);
  const hasEmailCol = parsed.headers.includes("email");
  const rowIssues = useMemo(
    () =>
      parsed.rows
        .map((r, i) => {
          const em = (r.email || "").toLowerCase();
          if (!em) return { line: i + 2, reason: "empty email" };
          if (!em.endsWith("@iltexas.org")) return { line: i + 2, reason: `${em} not @iltexas.org` };
          return null;
        })
        .filter(Boolean),
    [parsed.rows],
  );
  const validRows = useMemo(
    () => parsed.rows.filter((_, i) => !rowIssues.find((r) => r.line === i + 2)),
    [parsed.rows, rowIssues],
  );

  const canUpload = validRows.length > 0 && hasEmailCol && !processing;

  const upload = async () => {
    if (!canUpload) return;
    setProcessing(true);
    setError(null);
    setResult(null);
    try {
      const csv = rowsToCSV(parsed.headers, validRows);
      const blob = new Blob([csv], { type: "text/csv" });
      const fd = new FormData();
      fd.append("file", blob, "staff.csv");
      const res = await axios.post(`${BACKEND_URL}/api/staff/bulk-add`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult({ ...res.data, rejected_client_side: rowIssues.length });
      if (res.data?.added > 0) setTimeout(() => onSuccess?.(), 2000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || e.message || "Upload failed");
    } finally {
      setProcessing(false);
    }
  };

  const handleFile = async (f) => {
    if (!f) return;
    setText(await f.text());
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "staff_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <Card className="w-full max-w-3xl my-8">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Bulk Add Staff
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose} disabled={processing}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {result && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <AlertDescription className="text-green-900">
                <p className="font-semibold">Added {result.added}, skipped {result.skipped}
                {result.rejected_client_side ? `, rejected ${result.rejected_client_side}` : ""}.</p>
                {result.errors?.length > 0 && (
                  <details className="text-xs mt-1">
                    <summary className="cursor-pointer">{result.errors.length} row error(s)</summary>
                    <ul className="mt-1 list-disc list-inside">
                      {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </details>
                )}
              </AlertDescription>
            </Alert>
          )}

          <Alert className="bg-blue-50 border-blue-200">
            <AlertDescription className="text-sm text-blue-900 space-y-1">
              <p><strong>Required:</strong> email &nbsp;·&nbsp; <strong>Optional:</strong> name, user_type, teacher_grade_level</p>
              <p className="text-xs">user_type: <code>teacher</code>, <code>assistant_principal</code>, <code>ea_staff</code>, or <code>driveline</code> (defaults to teacher)</p>
              <p className="text-xs">teacher_grade_level: e.g. <code>6</code>, <code>7</code>, <code>K</code> — required for teachers who want to add students to EA</p>
              <p className="text-xs">Only <code>@iltexas.org</code> emails are accepted. Duplicates are skipped.</p>
            </AlertDescription>
          </Alert>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={downloadSample}>
              <Download className="w-4 h-4 mr-2" /> Download template
            </Button>
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={processing}>
              <FileUp className="w-4 h-4 mr-2" /> Upload CSV file
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.tsv,.txt"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>

          <div>
            <p className="text-sm font-medium text-slate-700 mb-1">Or paste your data below</p>
            <Textarea
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setError(null);
                setResult(null);
              }}
              placeholder={"email,name,user_type\nsomeone@iltexas.org,Some One,teacher"}
              className="h-40 font-mono text-xs"
              disabled={processing}
            />
          </div>

          {parsed.rows.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 border-b flex items-center gap-2 text-sm">
                <Eye className="w-4 h-4 text-slate-600" />
                <span className="font-semibold text-slate-700">Preview</span>
                <span className="text-xs text-slate-500">
                  ({Math.min(parsed.rows.length, 5)} of {parsed.rows.length} rows)
                </span>
              </div>
              {!hasEmailCol && (
                <div className="px-3 py-2 bg-red-50 border-b text-sm text-red-800">
                  Missing required <code>email</code> column.
                </div>
              )}
              {hasEmailCol && rowIssues.length > 0 && (
                <div className="px-3 py-2 bg-amber-50 border-b text-sm text-amber-900">
                  {rowIssues.length} row{rowIssues.length === 1 ? "" : "s"} will be rejected (invalid email).
                </div>
              )}
              <div className="overflow-x-auto max-h-56">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      {parsed.headers.slice(0, 4).map((h) => (
                        <th key={h} className="px-3 py-2 text-left font-semibold text-slate-700 border-b">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.slice(0, 5).map((r, i) => (
                      <tr key={i} className="border-b">
                        {parsed.headers.slice(0, 4).map((h) => (
                          <td key={h} className="px-3 py-2 text-slate-700">
                            {r[h] || <span className="text-slate-300 italic">empty</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={processing}>Close</Button>
            <Button onClick={upload} disabled={!canUpload} className="bg-blue-600 hover:bg-blue-700 text-white">
              {processing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
              ) : (
                <><CheckCircle className="w-4 h-4 mr-2" /> Add {validRows.length} Staff</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
